import { ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, finalize, forkJoin, map, of, switchMap, tap } from 'rxjs';
import {
  CreateProductRequest,
  MainProductCategory,
  Product,
  ProductBrand,
  ProductCategory,
  CustomerProfile,
  ProductEnumValue,
  ProductListingType,
  ProductStatus,
  ProductSubCategory,
  SellerBusiness,
} from '../../core/models/product.models';
import { BusinessLocationScope, BusinessService, BusinessStore } from '../../core/services/business.service';
import { ProductPage, ProductsService } from '../../core/services/products.service';
import { CustomersService } from '../../core/services/customers.service';
import { httpErrorMessage } from '../../core/utils/http-error-message';
import { ConfirmationService } from '../../core/services/confirmation.service';
import { OrdersService } from '../../core/services/orders.service';
import { CurrentUserService } from '../../core/auth/current-user.service';

type SellerType = 'Individual' | 'Business';

@Component({
  selector: 'app-products',
  imports: [FormsModule, DatePipe],
  templateUrl: './products.html',
  styleUrl: './products.scss',
})
export class Products implements OnInit, OnDestroy {
  private readonly productsService = inject(ProductsService);
  private readonly businessService = inject(BusinessService);
  private readonly customersService = inject(CustomersService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly confirmation = inject(ConfirmationService);
  private readonly ordersService = inject(OrdersService);
  private readonly currentUser = inject(CurrentUserService);
  get canEditProduct(): boolean { return this.currentUser.hasPermission('PRODUCT_UPDATE'); }
  get canManageProfitRules(): boolean { return ['PROFIT_RULE_CREATE', 'PROFIT_RULE_UPDATE', 'PROFIT_RULE_DELETE', 'PROFIT_RULE_STATUS_UPDATE'].some((permission) => this.currentUser.hasPermission(permission)); }
  private readonly productQuantities = new Map<string, number>();
  @ViewChild('descriptionEditor') private descriptionEditor?: ElementRef<HTMLElement>;
  readonly reviewOnly = this.route.snapshot.data['reviewOnly'] === true;
  readonly createOnly = this.route.snapshot.data['createOnly'] === true;

  readonly mainCategoryLabels: Record<number, MainProductCategory> = {
    1: 'Electronics',
    2: 'Vehicle',
  };
  readonly itemTypeLabels: Record<number, ProductSubCategory> = {
    1: 'Mobile',
    2: 'Laptop',
    3: 'Desktop',
    10: 'Bike',
    11: 'Car',
    12: 'Truck',
    13: 'Tractor',
  };
  readonly conditionLabels: Record<number, ProductListingType> = {
    1: 'New',
    2: 'Used',
  };
  readonly statusLabels: Record<number, string> = {
    1: 'Draft',
    2: 'Active',
    3: 'Sold',
    4: 'Inactive',
    5: 'Rejected',
    6: 'On hold',
    7: 'Reported',
    8: 'Pending review',
  };

  parentCategories: ProductCategory[] = [];
  allChildCategories: ProductCategory[] = [];
  formChildCategories: ProductCategory[] = [];
  formSubChildCategories: ProductCategory[] = [];
  filterChildCategories: ProductCategory[] = [];
  filterSubChildCategories: ProductCategory[] = [];
  formParentCategoryId = '';
  formSubCategoryId = '';
  filterParentCategoryId = '';
  filterSubCategoryId = '';
  filterCategoryId = '';
  brands: ProductBrand[] = [];
  catalogBrands: ProductBrand[] = [];
  stores: BusinessStore[] = [];
  sellerBusinesses: SellerBusiness[] = [];
  customers: CustomerProfile[] = [];
  products: Product[] = [];
  isLoading = false;
  isMetaLoading = false;
  isSubmitting = false;
  moderatingProductId = '';
  errorMessage = '';
  successMessage = '';
  successMessageTone: 'create' | 'edit' | 'danger' = 'create';
  isLocationPickerOpen = false;
  pendingStoreId = '';
  aadhaarNumber = '';
  aadhaarOtp = '';
  aadhaarReferenceId = '';
  isAadhaarBusy = false;
  currentStep: 1 | 2 = 1;
  sellerType: SellerType = 'Business';
  individualSellerPhone = '';
  isSellerLookupBusy = false;
  editingProduct: Product | null = null;
  isOpeningEdit = false;
  appealingProduct: Product | null = null;
  appealReason = '';
  isSubmittingAppeal = false;
  editImageFiles: File[] = [];
  editImagePreviewUrls: string[] = [];
  viewingProduct: Product | null = null;
  isSavingEdit = false;
  editForm = { title: '', description: '', modelName: '', cost: 0, repairCost: 0, extraCost: 0, sellingPrice: 0, isReadyToSell: true };
  productPageNumber = 1;
  readonly productPageSize = 10;
  totalProductCount = 0;
  isLoadingMore = false;
  filterStoreId = '';
  imagePreviews: Array<{ name: string; url: string }> = [];
  sellerImagePreviewUrl = '';
  billPreviewUrl = '';
  billIsPdf = false;
  readonly purchaseItemId = this.route.snapshot.queryParamMap.get('purchaseItemId') ?? '';

  filters = {
    search: '',
    listingType: '' as ProductListingType | '',
    mainCategory: 'Electronics' as MainProductCategory,
    subCategory: 'Mobile' as ProductSubCategory,
    sellerId: '',
    customerId: '',
  };

  form: CreateProductRequest = {
    title: '',
    description: '',
    mainCategory: 'Electronics',
    subCategory: 'Mobile',
    listingType: 'New',
    condition: 'New',
    cost: 0,
    repairCost: 0,
    extraCost: 0,
    isReadyToSell: true,
    currency: 'INR',
    sellerId: '',
    customerId: '',
    sellerTenantId: '',
    brandId: '',
    country: 'India',
    imageFiles: [],
  };

  ngOnInit(): void {
    if (this.createOnly && this.purchaseItemId) {
      const query = this.route.snapshot.queryParamMap;
      this.form.title = query.get('title') ?? '';
      this.form.modelName = query.get('modelName') ?? '';
      this.form.listingType = query.get('condition') === 'Used' ? 'Used' : 'New';
      this.form.condition = this.form.listingType;
      this.form.cost = Number(query.get('cost') ?? 0);
      this.form.repairCost = this.form.listingType === 'Used' ? Number(query.get('repairCost') ?? 0) : undefined;
      this.form.extraCost = Number(query.get('extraCost') ?? 0);
      this.form.storeId = query.get('storeId') ?? undefined;
      this.form.sellerUserId = query.get('sellerUserId') ?? undefined;
      this.form.existingSellerVerificationImageUrl = query.get('existingSellerVerificationImageUrl') ?? undefined;
      this.form.existingOriginalBillUrl = query.get('existingOriginalBillUrl') ?? undefined;
      try {
        const existingImages = JSON.parse(query.get('existingImageUrlsJson') ?? '[]');
        this.form.existingImageUrls = Array.isArray(existingImages) ? existingImages.filter((url): url is string => typeof url === 'string' && Boolean(url)) : [];
      } catch { this.form.existingImageUrls = []; }
    }
    this.loadMeta();
    if (!this.createOnly) this.loadProducts();
  }

  ngOnDestroy(): void {
    this.releaseImagePreviews();
    this.releaseEvidencePreviews();
  }

  get locationScope(): BusinessLocationScope {
    return this.router.url.startsWith('/admin') ? 'platform' : 'business';
  }

  get isPlatformMode(): boolean {
    return this.locationScope === 'platform';
  }

  get selectedCustomer(): CustomerProfile | undefined {
    return this.customers.find((customer) => customer.identityUserId === this.form.sellerUserId);
  }

  get isSelectedCustomerVerified(): boolean {
    const customer = this.selectedCustomer;
    return customer?.aadhaarVerificationStatus?.toUpperCase() === 'VERIFIED'
      && customer?.livenessVerificationStatus?.toUpperCase() === 'VERIFIED'
      && Boolean(customer.livenessExpiresAt && new Date(customer.livenessExpiresAt) > new Date());
  }

  get isSelectedCustomerAadhaarVerified(): boolean {
    return this.selectedCustomer?.aadhaarVerificationStatus?.toUpperCase() === 'VERIFIED';
  }

  get canCreateProduct(): boolean {
    if (!this.form.storeId) return false;
    if (this.purchaseItemId) return true;
    if (!this.isPlatformMode) return true;
    if (this.sellerType === 'Business') {
      return Boolean(
        this.form.sellerTenantId
        && this.form.sellerUserId
        && (this.form.listingType === 'New' || this.form.sellerVerificationImage || this.form.originalBill),
      );
    }
    return Boolean(
      this.form.sellerUserId
      && this.isSelectedCustomerVerified
      && (this.form.sellerVerificationImage || this.form.originalBill),
    );
  }

  get canContinueToVerification(): boolean {
    return Boolean(
      this.form.title.trim()
      && this.form.categoryId
      && Number(this.form.cost) > 0
      && (this.form.listingType === 'New' || Number(this.form.repairCost) >= 0),
    );
  }

  get sellerVerificationImageName(): string {
    return this.form.sellerVerificationImage?.name || 'Take seller photo with product';
  }

  get originalBillName(): string {
    return this.form.originalBill?.name || 'Upload original bill';
  }

  get selectedImageNames(): string {
    return this.form.imageFiles?.map((file) => file.name).join(', ') || 'No images selected';
  }

  get selectedStore(): BusinessStore | undefined {
    return this.stores.find((store) => store.id === this.form.storeId);
  }

  get selectedStoreAddress(): string {
    const store = this.selectedStore;
    if (!store) {
      return '';
    }

    return [store.addressLine2, store.addressLine1, store.area, store.city, store.state, store.postalCode, store.country]
      .filter(Boolean)
      .join(', ');
  }

  get visibleProducts(): Product[] {
    const search = this.filters.search.trim().toLowerCase();
    return this.products.filter((product) => {
      if (this.filterStoreId && product.storeId !== this.filterStoreId) return false;
      if (this.filters.listingType && this.displayCondition(product) !== this.filters.listingType) return false;
      if (this.filterCategoryId && product.categoryId !== this.filterCategoryId) return false;
      if (search && !`${product.title} ${product.description ?? ''} ${product.modelName ?? ''}`.toLowerCase().includes(search)) return false;
      return true;
    });
  }

  get hasMoreProducts(): boolean {
    return this.products.length < this.totalProductCount;
  }

  get needsProfitRuleSetup(): boolean {
    return this.errorMessage.toLowerCase().includes('no active profit rule');
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (!this.hasMoreProducts || this.isLoading) return;
    const remaining = document.documentElement.scrollHeight - (window.scrollY + window.innerHeight);
    if (remaining <= 320) {
      this.loadNextProductPage();
    }
  }

  loadProducts(): void {
    this.isLoading = true;
    this.productPageNumber = 1;
    this.errorMessage = '';
    this.cdr.detectChanges();

    const productsRequest = this.reviewOnly || this.isPlatformMode
      ? of({ products: [], totalCount: 0, pageNumber: 1, pageSize: 10 } as ProductPage)
      : this.productsService.listBySeller().pipe(map((products) => ({ products, totalCount: products.length, pageNumber: 1, pageSize: products.length || 10 } as ProductPage)));
    forkJoin({
      products: productsRequest,
      reviewQueue: this.isPlatformMode
        ? this.productsService.reviewQueue(this.reviewOnly ? 'pending' : 'all').pipe(map((result) => result.products))
        : of([] as Product[]),
      purchases: this.ordersService.purchases().pipe(catchError(() => of([] as any[]))),
    })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: ({ products: page, reviewQueue, purchases }) => {
          this.productQuantities.clear();
          for (const purchase of purchases) {
            const items = Array.isArray(purchase?.items) ? purchase.items : Array.isArray(purchase?.purchaseItems) ? purchase.purchaseItems : [];
            for (const item of items) {
              if (!item?.productId) continue;
              const available = Number(item.availableQuantity ?? item.quantity ?? 0);
              this.productQuantities.set(item.productId, (this.productQuantities.get(item.productId) ?? 0) + available);
            }
          }
          this.products = this.isPlatformMode ? reviewQueue : page.products;
          this.totalProductCount = this.isPlatformMode ? reviewQueue.length : page.totalCount;
          this.productPageNumber = page.pageNumber;
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (error: unknown) => {
          this.isLoading = false;
          this.errorMessage = httpErrorMessage(error, 'Products API is unavailable.');
          this.cdr.detectChanges();
        },
      });
  }

  private loadNextProductPage(): void {
    if (this.isLoadingMore || !this.hasMoreProducts || this.reviewOnly) return;
    this.isLoadingMore = true;
    const nextPage = this.productPageNumber + 1;
    this.productsService.listPage({
      search: this.filters.search,
      listingType: this.filters.listingType || undefined,
      categoryId: this.filterCategoryId || undefined,
      mainCategory: this.filters.mainCategory,
      subCategory: this.filters.subCategory,
      sellerId: this.filters.sellerId || undefined,
      customerId: this.filters.customerId || undefined,
      pageNumber: nextPage,
      pageSize: this.productPageSize,
    }).pipe(finalize(() => {
      this.isLoadingMore = false;
      this.cdr.detectChanges();
    })).subscribe({
      next: (page) => {
        const existing = new Set(this.products.map((product) => product.id));
        this.products = [...this.products, ...page.products.filter((product) => !existing.has(product.id))];
        this.productPageNumber = page.pageNumber;
        this.totalProductCount = page.totalCount;
        this.cdr.detectChanges();
      },
      error: (error: unknown) => {
        this.errorMessage = httpErrorMessage(error, 'Unable to load more products.');
        this.cdr.detectChanges();
      },
    });
  }

  moderateProduct(product: Product, action: 'Approve' | 'Hold' | 'Reject' | 'Deactivate', reason?: string): void {
    let moderationReason = reason?.trim();
    if ((action === 'Reject' || action === 'Hold') && !moderationReason) {
      const enteredReason = window.prompt(
        action === 'Reject' ? `Why are you rejecting “${product.title}”?` : `Why are you putting “${product.title}” on hold?`,
        this.productModerationReason(product),
      );
      if (enteredReason === null) return;
      moderationReason = enteredReason.trim();
      if (!moderationReason) {
        this.errorMessage = `A reason is required to ${action.toLowerCase()} this product.`;
        return;
      }
    }
    this.moderatingProductId = product.id;
    this.errorMessage = '';
    this.successMessage = '';
    this.productsService.moderate(product.id, action, moderationReason)
      .pipe(finalize(() => (this.moderatingProductId = '')))
      .subscribe({
        next: () => {
          const nextStatus: ProductStatus = action === 'Approve' ? 'Active' : action === 'Hold' ? 'OnHold' : action === 'Reject' ? 'Rejected' : 'Inactive';
          if (this.reviewOnly) this.products = this.products.filter((item) => item.id !== product.id);
          else this.products = this.products.map((item) => item.id === product.id
            ? { ...item, status: nextStatus, moderationNote: moderationReason, moderationReason }
            : item);
          this.successMessageTone = action === 'Approve' ? 'create' : action === 'Hold' ? 'edit' : 'danger';
          this.successMessage = action === 'Approve'
            ? `${product.title} is approved and live.`
            : action === 'Hold'
              ? `${product.title} is now on hold.`
              : action === 'Reject'
                ? `${product.title} was rejected.`
                : `${product.title} is now inactive.`;
        },
        error: (error: unknown) => {
          this.errorMessage = httpErrorMessage(error, `Unable to ${action.toLowerCase()} listing.`);
        },
      });
  }

  async openEditProduct(product: Product): Promise<void> {
    if (this.isOpeningEdit || this.editingProduct || this.isSavingEdit) return;
    this.isOpeningEdit = true;
    this.cdr.detectChanges();
    try {
      const confirmed = await this.confirmation.confirmDestructive(
        'Edit this product?',
        `You are about to change “${product.title}”. Review the values carefully before saving.`,
        'Continue to edit',
      );
      if (!confirmed) return;
      this.clearEditImages();
      this.editForm = {
        title: product.title,
        description: product.description ?? '',
        modelName: product.modelName ?? '',
        cost: Number(product.cost ?? 0),
        repairCost: Number(product.repairCost ?? 0),
        extraCost: Number(product.extraCost ?? 0),
        sellingPrice: Number(product.price ?? 0),
        isReadyToSell: product.isReadyToSell !== false,
      };
      this.editingProduct = product;
    } finally {
      this.isOpeningEdit = false;
      this.cdr.detectChanges();
    }
  }

  openProductView(product: Product): void {
    this.viewingProduct = product;
  }

  closeProductView(): void {
    this.viewingProduct = null;
  }

  editViewedProduct(): void {
    const product = this.viewingProduct;
    if (!product) return;
    this.closeProductView();
    void this.openEditProduct(product);
  }

  closeEditProduct(): void {
    if (this.isSavingEdit) return;
    this.clearEditImages();
    this.editingProduct = null;
  }

  onEditImagesChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selected = Array.from(input.files ?? []);
    this.editImageFiles = [...this.editImageFiles, ...selected];
    this.editImagePreviewUrls = [...this.editImagePreviewUrls, ...selected.map(file => URL.createObjectURL(file))];
    input.value = '';
  }

  removeEditImage(index: number, input: HTMLInputElement): void {
    URL.revokeObjectURL(this.editImagePreviewUrls[index]);
    this.editImageFiles.splice(index, 1);
    this.editImagePreviewUrls.splice(index, 1);
    this.editImageFiles = [...this.editImageFiles];
    this.editImagePreviewUrls = [...this.editImagePreviewUrls];
    if (!this.editImageFiles.length) input.value = '';
  }

  private clearEditImages(): void {
    this.editImagePreviewUrls.forEach(url => URL.revokeObjectURL(url));
    this.editImageFiles = [];
    this.editImagePreviewUrls = [];
  }

  saveEditedProduct(): void {
    const product = this.editingProduct;
    if (!product || this.isSavingEdit) return;
    if (this.editForm.title.trim().length < 5 || Number(this.editForm.sellingPrice) <= 0) {
      this.errorMessage = 'Enter a title with at least 5 characters and a selling price greater than zero.';
      return;
    }
    this.isSavingEdit = true;
    this.productsService.updateAdmin(product.id, { ...this.editForm, images: this.editImageFiles }).pipe(finalize(() => {
      this.isSavingEdit = false;
      this.cdr.detectChanges();
    })).subscribe({
      next: (updated) => {
        this.products = this.products.map((item) => item.id === updated.id ? updated : item);
        this.successMessageTone = 'edit';
        this.successMessage = `${updated.title} was updated.`;
        this.isSavingEdit = false;
        this.clearEditImages();
        this.editingProduct = null;
        this.cdr.detectChanges();
      },
      error: (error: unknown) => {
        this.errorMessage = httpErrorMessage(error, 'Unable to update the product.');
        this.cdr.detectChanges();
      },
    });
  }

  async deactivateProduct(product: Product): Promise<void> {
    const confirmed = await this.confirmation.confirmDestructive(
      'Make product inactive?',
      `“${product.title}” will be removed from customer product listings.`,
      'Make inactive',
    );
    if (!confirmed) return;
    this.moderatingProductId = product.id;
    this.productsService.moderate(product.id, 'Deactivate', 'Deactivated by administrator.').pipe(finalize(() => {
      this.moderatingProductId = '';
      this.cdr.detectChanges();
    })).subscribe({
      next: () => {
        this.products = this.products.map((item) => item.id === product.id ? { ...item, status: 'Inactive' } : item);
        this.successMessageTone = 'danger';
        this.successMessage = `${product.title} is now inactive.`;
        this.cdr.detectChanges();
      },
      error: (error: unknown) => {
        this.errorMessage = httpErrorMessage(error, 'Unable to make the product inactive.');
        this.cdr.detectChanges();
      },
    });
  }

  listSellerProducts(): void {
    this.filters.customerId = '';
    this.loadProducts();
  }

  listCustomerOldProducts(): void {
    this.filters.listingType = 'Used';
    this.filters.sellerId = '';
    this.loadProducts();
  }

  createProduct(): void {
    if (!this.form.categoryId) {
      this.errorMessage = 'Select a category and subcategory before creating the product.';
      return;
    }

    if (Number(this.form.cost) <= 0) {
      this.errorMessage = 'Cost must be greater than zero.';
      return;
    }
    if (this.form.listingType === 'Used' && (this.form.repairCost == null || Number(this.form.repairCost) < 0)) {
      this.errorMessage = 'Enter repair cost. It can be zero when no repair is required.';
      return;
    }

    if (!this.form.storeId) {
      this.errorMessage = 'Select a business location before creating the product.';
      return;
    }

    if (this.isPlatformMode && this.sellerType === 'Business' && !this.purchaseItemId && (!this.form.sellerTenantId || !this.form.sellerUserId)) {
      this.errorMessage = 'Select a seller business before creating the product.';
      return;
    }
    if (this.isPlatformMode && this.form.listingType === 'Used' && !this.purchaseItemId && this.sellerType === 'Individual' && !this.isSelectedCustomerVerified) {
      this.errorMessage = 'Select an Aadhaar-verified customer before creating the used product.';
      return;
    }
    if (this.isPlatformMode && this.form.listingType === 'Used' && !this.purchaseItemId && !this.form.sellerVerificationImage && !this.form.originalBill) {
      this.errorMessage = 'Provide a seller photo with the product or upload the original bill.';
      return;
    }

    this.applyStoreLocation(this.form.storeId);
    this.form.description = this.sanitizeDescription(this.form.description ?? '');
    this.form.currency = 'INR';
    this.isSubmitting = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.productsService
      .create(this.form)
      .pipe(finalize(() => {
        this.isSubmitting = false;
        this.cdr.detectChanges();
      }))
      .subscribe({
        next: (product) => {
          this.isSubmitting = false;
          this.products = [product, ...this.products];
          this.successMessageTone = 'create';
          this.successMessage = 'Product listing created.';
          if (this.purchaseItemId) {
            this.ordersService.linkPurchaseItem(this.purchaseItemId, product.id).subscribe({
              next: () => this.successMessage = 'Product listing created and linked to purchased inventory.',
              error: () => this.errorMessage = 'Listing was created, but inventory linking failed. Please retry from Inventory.',
            });
          }
          this.form = {
            ...this.form,
            title: '',
            description: '',
            cost: 0,
            repairCost: 0,
            extraCost: 0,
            brandId: '',
            imageFiles: [],
            sellerVerificationImage: undefined,
            originalBill: undefined,
          };
          this.releaseImagePreviews();
          this.releaseEvidencePreviews();
          this.currentStep = 1;
          if (this.descriptionEditor) this.descriptionEditor.nativeElement.innerHTML = '';
          this.cdr.detectChanges();
        },
        error: (error: unknown) => {
          this.isSubmitting = false;
          this.errorMessage = httpErrorMessage(error, 'Unable to create product listing.');
          this.cdr.detectChanges();
        },
      });
  }

  onProductTypeChange(): void {
    if (this.form.listingType === 'New') {
      this.sellerType = 'Business';
      this.form.repairCost = undefined;
    } else {
      this.form.repairCost ??= 0;
      this.sellerType = 'Individual';
    }
    if (!this.isPlatformMode) return;

    this.form.storeId = undefined;
    this.form.sellerUserId = undefined;
    this.form.sellerTenantId = '';
    this.resetAadhaarForm();
    if (this.form.listingType === 'Used') {
      this.loadPlatformStores();
    } else {
      this.stores = [];
    }
  }

  onSellerBusinessChange(tenantId: string): void {
    const business = this.sellerBusinesses.find((item) => item.id === tenantId);
    this.form.sellerTenantId = business?.id;
    this.form.sellerUserId = business?.ownerUserId ?? undefined;
  }

  onSellerTypeChange(): void {
    this.form.sellerUserId = undefined;
    this.form.sellerTenantId = '';
    this.customers = [];
    this.individualSellerPhone = '';
    this.resetAadhaarForm();
  }

  onIndividualSellerPhoneInput(value: string): void {
    this.individualSellerPhone = value.replace(/\D/g, '').slice(0, 10);
  }

  startIndividualVerification(): void {
    const phone = this.individualSellerPhone.replace(/\D/g, '');
    if (!/^\d{10}$/.test(phone)) {
      this.errorMessage = 'Enter the registered 10-digit mobile number.';
      this.cdr.detectChanges();
      return;
    }
    if (!/^\d{12}$/.test(this.aadhaarNumber)) {
      this.errorMessage = 'Enter a valid 12-digit Aadhaar number.';
      return;
    }

    this.isSellerLookupBusy = true;
    this.isAadhaarBusy = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.customersService.lookupByPhone(phone).pipe(
      tap((customer) => {
          this.customers = [customer];
          this.form.sellerUserId = customer.identityUserId;
          this.form.sellerTenantId = '';
      }),
      switchMap((customer) => this.isSelectedCustomerAadhaarVerified
        ? of(null)
        : this.customersService.generateAadhaarOtp(customer.identityUserId, this.aadhaarNumber)),
      finalize(() => {
        this.isSellerLookupBusy = false;
        this.isAadhaarBusy = false;
        this.cdr.detectChanges();
      }),
    ).subscribe({
        next: (result) => {
          if (!result) {
            this.aadhaarNumber = '';
            this.successMessage = 'Seller identity is already Aadhaar verified.';
            return;
          }

          this.aadhaarReferenceId = result.referenceId;
          this.updateSelectedCustomer({ maskedAadhaarNumber: result.maskedAadhaarNumber, aadhaarVerificationStatus: 'PENDING' });
          this.successMessage = result.message || 'Seller found and Aadhaar OTP sent.';
          this.cdr.detectChanges();
        },
        error: (error: unknown) => {
          this.customers = [];
          this.form.sellerUserId = undefined;
          this.errorMessage = httpErrorMessage(error, 'Unable to find or verify the individual seller.');
          this.cdr.detectChanges();
        },
      });
  }

  onCustomerChange(): void {
    this.form.sellerTenantId = '';
    this.resetAadhaarForm();
  }

  verifyAadhaarOtp(): void {
    if (!this.form.sellerUserId || !this.aadhaarReferenceId || !/^\d{4,8}$/.test(this.aadhaarOtp)) {
      this.errorMessage = 'Enter the OTP sent for Aadhaar verification.';
      return;
    }
    this.isAadhaarBusy = true;
    this.errorMessage = '';
    this.customersService.verifyAadhaarOtp(this.form.sellerUserId, this.aadhaarReferenceId, this.aadhaarOtp)
      .pipe(finalize(() => (this.isAadhaarBusy = false)))
      .subscribe({
        next: (result) => {
          this.updateSelectedCustomer({ aadhaarVerificationStatus: 'VERIFIED', aadhaarVerifiedName: result.verifiedName });
          this.aadhaarNumber = '';
          this.aadhaarOtp = '';
          this.aadhaarReferenceId = '';
          this.successMessage = result.message || 'Customer Aadhaar verified.';
        },
        error: (error: unknown) => this.errorMessage = httpErrorMessage(error, 'Unable to verify Aadhaar OTP.'),
      });
  }

  onFilterCategoryChange(): void {
    const parent = this.parentCategories.find((category) => category.id === this.filterParentCategoryId);
    if (!parent) {
      this.filterChildCategories = [];
      this.filterCategoryId = '';
      return;
    }

    this.filters.mainCategory = this.displayMainCategory(parent.mainCategory) as MainProductCategory;
    this.filterCategoryId = '';
    this.filterSubCategoryId = '';
    this.filterSubChildCategories = [];
    this.filterChildCategories = this.allChildCategories.filter((category) => category.parentCategoryId === parent.id);
    const first = this.filterChildCategories[0];
    if (first) {
      this.filterSubCategoryId = first.id;
      this.onFilterSubCategoryChange(first.id);
    }
  }

  onFormCategoryChange(): void {
    const parent = this.parentCategories.find((category) => category.id === this.formParentCategoryId);
    this.form.categoryId = undefined;
    this.formSubCategoryId = '';
    this.form.brandId = '';
    this.formChildCategories = [];
    this.formSubChildCategories = [];
    this.brands = [];
    if (!parent) {
      return;
    }

    this.applyCategoryToForm(parent);
    this.formChildCategories = this.allChildCategories.filter((category) => category.parentCategoryId === parent.id);
    const first = this.formChildCategories[0];
    this.formSubCategoryId = first?.id ?? '';
    if (first) {
      this.onFormSubCategoryChange();
    } else {
      this.form.categoryId = parent.id;
      this.loadBrands(parent.id);
    }
  }

  onFormSubCategoryChange(): void {
    const category = this.formChildCategories.find((item) => item.id === this.formSubCategoryId);
    this.formSubChildCategories = this.allChildCategories.filter((item) => item.parentCategoryId === category?.id);
    const selected = this.formSubChildCategories[0] ?? category;
    this.form.categoryId = selected?.id;
    if (selected) this.applyCategoryToForm(selected);
    this.loadBrands(this.form.categoryId);
  }

  onFormSubChildCategoryChange(): void {
    const category = this.formSubChildCategories.find((item) => item.id === this.form.categoryId);
    if (category) this.applyCategoryToForm(category);
    this.loadBrands(this.form.categoryId);
  }

  onFilterSubCategoryChange(categoryId: string): void {
    const category = this.filterChildCategories.find((item) => item.id === categoryId);
    if (!category) {
      return;
    }

    this.filterSubChildCategories = this.allChildCategories.filter((item) => item.parentCategoryId === category.id);
    const selected = this.filterSubChildCategories[0] ?? category;
    this.filterCategoryId = selected.id;
    const itemType = this.displayItemType(selected.itemType ?? category.itemType) as ProductSubCategory;
    if (this.isSubCategory(itemType)) {
      this.filters.subCategory = itemType;
    }
  }

  onFilterSubChildCategoryChange(categoryId: string): void {
    const category = this.filterSubChildCategories.find((item) => item.id === categoryId);
    const itemType = this.displayItemType(category?.itemType) as ProductSubCategory;
    if (this.isSubCategory(itemType)) this.filters.subCategory = itemType;
  }

  onImageFilesChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    this.releaseImagePreviews();
    this.form.imageFiles = files;
    this.imagePreviews = files.map((file) => ({
      name: file.name,
      url: URL.createObjectURL(file),
    }));
  }

  removeProductImage(index: number, input: HTMLInputElement): void {
    const preview = this.imagePreviews[index];
    if (preview) URL.revokeObjectURL(preview.url);
    this.imagePreviews.splice(index, 1);
    this.form.imageFiles?.splice(index, 1);

    const transfer = new DataTransfer();
    for (const file of this.form.imageFiles ?? []) transfer.items.add(file);
    input.files = transfer.files;
  }

  formatDescription(command: string, value?: string): void {
    document.execCommand(command, false, value);
  }

  onDescriptionInput(event: Event): void {
    this.form.description = (event.currentTarget as HTMLElement).innerHTML;
  }

  onDescriptionPaste(event: ClipboardEvent): void {
    event.preventDefault();
    document.execCommand('insertText', false, event.clipboardData?.getData('text/plain') ?? '');
  }

  descriptionText(description: string | undefined): string {
    if (!description) return 'No description';
    const container = document.createElement('div');
    container.innerHTML = description;
    return container.textContent?.trim() || 'No description';
  }

  onSellerVerificationImageChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (this.sellerImagePreviewUrl) URL.revokeObjectURL(this.sellerImagePreviewUrl);
    this.form.sellerVerificationImage = file;
    this.sellerImagePreviewUrl = file ? URL.createObjectURL(file) : '';
  }

  clearSellerVerificationImage(input: HTMLInputElement): void {
    if (this.sellerImagePreviewUrl) URL.revokeObjectURL(this.sellerImagePreviewUrl);
    this.sellerImagePreviewUrl = '';
    this.form.sellerVerificationImage = undefined;
    input.value = '';
  }

  onOriginalBillChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (this.billPreviewUrl) URL.revokeObjectURL(this.billPreviewUrl);
    this.form.originalBill = file;
    this.billIsPdf = file?.type === 'application/pdf' || file?.name.toLowerCase().endsWith('.pdf') === true;
    this.billPreviewUrl = file ? URL.createObjectURL(file) : '';
  }

  clearOriginalBill(input: HTMLInputElement): void {
    if (this.billPreviewUrl) URL.revokeObjectURL(this.billPreviewUrl);
    this.billPreviewUrl = '';
    this.billIsPdf = false;
    this.form.originalBill = undefined;
    input.value = '';
  }

  openProfitRuleSetup(): void {
    if (!this.form.categoryId) return;
    void this.router.navigate(['/admin/catalog'], {
      queryParams: {
        tab: 'profit',
        categoryId: this.form.categoryId,
        minimumProductValue: Number(this.form.cost || 0) + Number(this.form.repairCost || 0) + Number(this.form.extraCost || 0),
      },
    });
  }

  goToSellerVerification(): void {
    if (!this.canContinueToVerification) {
      this.errorMessage = 'Enter the product title and select its complete category.';
      return;
    }
    this.errorMessage = '';
    this.currentStep = 2;
  }

  backToProductDetails(): void {
    this.errorMessage = '';
    this.currentStep = 1;
  }

  applyStoreLocation(storeId: string | undefined): void {
    const store = this.stores.find((item) => item.id === storeId);
    if (!store) {
      return;
    }

    this.form.area = store.area ?? '';
    this.form.addressLine1 = store.addressLine1 ?? '';
    this.form.city = store.city ?? '';
    this.form.state = store.state ?? '';
    this.form.country = store.country || 'India';
    this.form.zipCode = store.postalCode ?? '';
    this.form.latitude = store.latitude ?? undefined;
    this.form.longitude = store.longitude ?? undefined;
  }

  changeLocation(): void {
    if (this.stores.length === 0) {
      return;
    }

    this.pendingStoreId = this.form.storeId ?? '';
    this.isLocationPickerOpen = true;
  }

  closeLocationPicker(): void {
    this.isLocationPickerOpen = false;
    this.pendingStoreId = '';
  }

  confirmLocationChange(): void {
    if (!this.pendingStoreId) {
      return;
    }

    this.form.storeId = this.pendingStoreId;
    this.applyStoreLocation(this.pendingStoreId);
    this.closeLocationPicker();
  }

  formatPrice(price: number): string {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(price) || 0);
  }

  productImageUrl(product: Product): string {
    const image = product.images?.find(Boolean);
    if (!image) {
      return '';
    }

    if (typeof image === 'string') {
      return image;
    }

    return image.imageUrl || image.url || image.fileUrl || '';
  }

  imageUrl(image: NonNullable<Product['images']>[number]): string {
    if (typeof image === 'string') return image;
    return image.imageUrl || image.url || image.fileUrl || '';
  }

  displayMainCategory(value: ProductEnumValue<MainProductCategory> | null | undefined): string {
    return this.displayEnum(value, this.mainCategoryLabels);
  }

  displayItemType(value: ProductEnumValue<ProductSubCategory> | null | undefined): string {
    return this.displayEnum(value, this.itemTypeLabels);
  }

  displayCondition(product: Product): string {
    return this.displayEnum(product.condition ?? product.listingType, this.conditionLabels);
  }

  displayQuantity(product: Product): string {
    if (this.displayCondition(product) !== 'New') return '—';
    return String(product.availableQuantity ?? product.quantity ?? this.productQuantities.get(product.id) ?? 0);
  }

  displayStatus(value: Product['status']): string {
    return this.displayEnum(value, this.statusLabels);
  }

  statusClass(value: Product['status']): 'green' | 'orange' | 'red' | 'blue' {
    const status = this.displayStatus(value).toLowerCase();
    if (status === 'active' || status === 'sold') return 'green';
    if (status === 'inactive' || status === 'on hold' || status === 'onhold' || status === 'draft' || status === 'pending review' || status === 'pendingreview') return 'orange';
    if (status === 'rejected' || status === 'reported' || status === 'flagged') return 'red';
    return 'blue';
  }

  productModerationReason(product: Product): string {
    return product.moderationReason?.trim() || product.moderationNote?.trim() || '';
  }

  onManualStatusChange(product: Product, event: Event): void {
    const select = event.target as HTMLSelectElement;
    const action = select.value as 'Approve' | 'Hold' | 'Reject' | 'Deactivate' | '';
    select.value = '';
    if (!action) return;
    this.moderateProduct(product, action);
  }

  appealProduct(product: Product): void {
    this.appealingProduct = product;
    this.appealReason = '';
  }

  closeAppeal(): void {
    if (this.isSubmittingAppeal) return;
    this.appealingProduct = null;
    this.appealReason = '';
  }

  submitAppeal(): void {
    const product = this.appealingProduct;
    const reason = this.appealReason.trim();
    if (!product) return;
    if (reason.length < 10) {
      this.errorMessage = 'Explain what you corrected in at least 10 characters before submitting the appeal.';
      return;
    }
    this.moderatingProductId = product.id;
    this.isSubmittingAppeal = true;
    this.errorMessage = '';
    this.productsService.appealModeration(product.id, reason).pipe(finalize(() => {
      this.moderatingProductId = '';
      this.isSubmittingAppeal = false;
      this.cdr.detectChanges();
    })).subscribe({
      next: () => {
        this.products = this.products.map((item) => item.id === product.id ? { ...item, status: 'PendingReview', automatedDecision: 'PendingReview', moderationReason: 'Appeal submitted and waiting for moderator review.', moderationNote: 'Appeal submitted and waiting for moderator review.' } : item);
        this.successMessageTone = 'edit';
        this.successMessage = `${product.title} is pending review. Your appeal was submitted successfully.`;
        this.closeAppeal();
      },
      error: (error: unknown) => this.errorMessage = httpErrorMessage(error, 'Unable to submit the appeal.'),
    });
  }

  displayLocation(product: Product): string {
    const store = this.productBusinessLocation(product);
    return [
      product.area || store?.area,
      product.city || store?.city,
      product.state || store?.state,
      product.zipCode || store?.postalCode,
      product.country || store?.country || 'India',
    ]
      .filter(Boolean)
      .join(', ');
  }

  productBusinessLocation(product: Product): BusinessStore | undefined {
    return this.stores.find((store) => store.id === product.storeId);
  }

  productLocationName(product: Product): string {
    return this.productBusinessLocation(product)?.name ?? 'Unassigned';
  }

  productStreetAddress(product: Product): string {
    const store = this.productBusinessLocation(product);
    return [store?.addressLine2, store?.addressLine1 ?? product.addressLine1].filter(Boolean).join(', ') || 'No address';
  }

  productCategoryName(product: Product): string {
    return [...this.parentCategories, ...this.allChildCategories].find((category) => category.id === product.categoryId)?.name
      ?? `${this.displayMainCategory(product.mainCategory)} / ${this.displayItemType(product.subCategory || product.itemType)}`;
  }

  productBrandName(product: Product): string {
    return this.catalogBrands.find((brand) => brand.id === product.brandId)?.name ?? 'No brand';
  }

  private loadMeta(): void {
    this.isMetaLoading = true;
    this.productsService.categories().subscribe({
      next: (categories) => {
        this.parentCategories = categories.filter((category): category is ProductCategory => 'id' in category);
        if (!this.parentCategories.length) return;
        forkJoin(this.parentCategories.map((category) => this.productsService.childCategories(category.id))).subscribe({
          next: (groups) => {
            const subcategories = groups.flat();
            if (!subcategories.length) {
              this.allChildCategories = [];
              this.initializeCategorySelections();
              return;
            }
            forkJoin(subcategories.map((category) => this.productsService.childCategories(category.id))).subscribe({
              next: (subGroups) => {
                this.allChildCategories = [...subcategories, ...subGroups.flat()];
                this.initializeCategorySelections();
              },
              error: () => {
                this.allChildCategories = subcategories;
                this.initializeCategorySelections();
              },
            });
          },
          error: () => {
            this.allChildCategories = [];
            this.errorMessage = 'Unable to load product subcategories.';
          },
        });
      },
      error: () => {
        this.parentCategories = [];
        this.formChildCategories = [];
        this.brands = [];
        this.errorMessage = 'Unable to load product categories.';
      },
    });

    this.productsService.brands().subscribe({
      next: (brands) => (this.catalogBrands = brands),
      error: () => (this.catalogBrands = []),
    });

    if (this.isPlatformMode) {
      this.businessService.businesses().subscribe({
        next: (businesses) => (this.sellerBusinesses = businesses.filter((business) => business.isActive && business.ownerUserId)),
        error: () => (this.sellerBusinesses = []),
      });
    }

    this.businessService
      .stores(this.locationScope)
      .pipe(finalize(() => (this.isMetaLoading = false)))
      .subscribe({
        next: (stores) => {
          this.isMetaLoading = false;
          this.stores = stores;
          if (!this.form.storeId) {
            const defaultStore = stores.find((store) => store.isPrimary) ?? stores[0];
            if (defaultStore) {
              this.filterStoreId ||= defaultStore.id;
              this.form.storeId = defaultStore.id;
              this.applyStoreLocation(defaultStore.id);
            }
          }
          this.cdr.detectChanges();
        },
        error: (error: unknown) => {
          this.isMetaLoading = false;
          this.stores = [];
          this.errorMessage = httpErrorMessage(error, 'Unable to load business locations.');
          this.cdr.detectChanges();
        },
      });
  }

  private loadPlatformStores(): void {
    this.isMetaLoading = true;
    this.businessService.stores('platform', true).pipe(finalize(() => (this.isMetaLoading = false))).subscribe({
      next: (stores) => {
        this.isMetaLoading = false;
        this.stores = stores;
        const location = stores.find((store) => store.isPrimary) ?? stores[0];
        if (location) {
          this.form.storeId = location.id;
          this.applyStoreLocation(location.id);
        }
        this.cdr.detectChanges();
      },
      error: (error: unknown) => {
        this.isMetaLoading = false;
        this.errorMessage = httpErrorMessage(error, 'Unable to load platform locations.');
        this.cdr.detectChanges();
      },
    });
  }

  private resetAadhaarForm(): void {
    this.aadhaarNumber = '';
    this.aadhaarOtp = '';
    this.aadhaarReferenceId = '';
  }

  private updateSelectedCustomer(changes: Partial<CustomerProfile>): void {
    this.customers = this.customers.map((customer) =>
      customer.identityUserId === this.form.sellerUserId ? { ...customer, ...changes } : customer,
    );
  }

  private sanitizeDescription(value: string): string {
    const documentValue = new DOMParser().parseFromString(value, 'text/html');
    const allowedTags = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'UL', 'OL', 'LI', 'H3', 'H4']);
    const clean = (node: Node): void => {
      for (const child of Array.from(node.childNodes)) {
        if (child.nodeType === Node.ELEMENT_NODE) {
          const element = child as HTMLElement;
          clean(element);
          if (!allowedTags.has(element.tagName)) {
            element.replaceWith(...Array.from(element.childNodes));
          } else {
            for (const attribute of Array.from(element.attributes)) element.removeAttribute(attribute.name);
          }
        } else if (child.nodeType !== Node.TEXT_NODE) {
          child.remove();
        }
      }
    };
    clean(documentValue.body);
    return documentValue.body.innerHTML.trim();
  }

  private loadBrands(categoryId?: string): void {
    this.form.brandId = '';
    this.productsService.brands(categoryId).subscribe({
      next: (brands) => {
        this.brands = brands;
      },
      error: () => {
        this.brands = [];
      },
    });
  }

  private releaseImagePreviews(): void {
    for (const preview of this.imagePreviews) URL.revokeObjectURL(preview.url);
    this.imagePreviews = [];
  }

  private releaseEvidencePreviews(): void {
    if (this.sellerImagePreviewUrl) URL.revokeObjectURL(this.sellerImagePreviewUrl);
    if (this.billPreviewUrl) URL.revokeObjectURL(this.billPreviewUrl);
    this.sellerImagePreviewUrl = '';
    this.billPreviewUrl = '';
    this.billIsPdf = false;
  }

  private initializeCategorySelections(): void {
    const first = this.parentCategories[0];
    if (!first) return;
    this.form.mainCategory = this.displayMainCategory(first.mainCategory) as MainProductCategory;
    this.filters.mainCategory = this.form.mainCategory;
    this.formParentCategoryId = first.id;
    this.filterParentCategoryId = first.id;
    this.onFormCategoryChange();
    this.onFilterCategoryChange();
  }

  private applyCategoryToForm(category: ProductCategory): void {
    const mainCategory = this.displayMainCategory(category.mainCategory) as MainProductCategory;
    const itemType = this.displayItemType(category.itemType) as ProductSubCategory;
    if (this.isMainCategory(mainCategory)) {
      this.form.mainCategory = mainCategory;
    }
    if (this.isSubCategory(itemType)) {
      this.form.itemType = itemType;
      this.form.subCategory = itemType;
    }
  }

  private displayEnum<T extends string>(
    value: ProductEnumValue<T> | string | number | null | undefined,
    labels: Record<number, string>,
  ): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    if (typeof value === 'number') {
      return labels[value] ?? String(value);
    }

    const numeric = Number(value);
    if (!Number.isNaN(numeric) && value.trim() !== '') {
      return labels[numeric] ?? value;
    }

    return value;
  }

  private isMainCategory(value: string): value is MainProductCategory {
    return value === 'Electronics' || value === 'Vehicle';
  }

  private isSubCategory(value: string): value is ProductSubCategory {
    return ['Mobile', 'Laptop', 'Desktop', 'Bike', 'Car', 'Truck', 'Tractor'].includes(value);
  }
}
